import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, Check, X } from "lucide-react";

// Google Translate-powered language switching.
// Popular languages first (based on DMV demographics + global reach).
export const LANGUAGES = [
  { code: "en", label: "English", native: "English", flag: "🇺🇸", popular: true },
  { code: "es", label: "Spanish", native: "Español", flag: "🇪🇸", popular: true },
  { code: "zh-CN", label: "Chinese (Simplified)", native: "简体中文", flag: "🇨🇳", popular: true },
  { code: "ko", label: "Korean", native: "한국어", flag: "🇰🇷", popular: true },
  { code: "vi", label: "Vietnamese", native: "Tiếng Việt", flag: "🇻🇳", popular: true },
  { code: "ar", label: "Arabic", native: "العربية", flag: "🇸🇦", popular: true },
  { code: "fr", label: "French", native: "Français", flag: "🇫🇷", popular: true },
  { code: "hi", label: "Hindi", native: "हिन्दी", flag: "🇮🇳", popular: true },
  // Long tail
  { code: "pt", label: "Portuguese", native: "Português", flag: "🇵🇹" },
  { code: "ru", label: "Russian", native: "Русский", flag: "🇷🇺" },
  { code: "de", label: "German", native: "Deutsch", flag: "🇩🇪" },
  { code: "it", label: "Italian", native: "Italiano", flag: "🇮🇹" },
  { code: "ja", label: "Japanese", native: "日本語", flag: "🇯🇵" },
  { code: "tl", label: "Tagalog", native: "Tagalog", flag: "🇵🇭" },
  { code: "fa", label: "Persian", native: "فارسی", flag: "🇮🇷" },
  { code: "ur", label: "Urdu", native: "اردو", flag: "🇵🇰" },
  { code: "tr", label: "Turkish", native: "Türkçe", flag: "🇹🇷" },
  { code: "pl", label: "Polish", native: "Polski", flag: "🇵🇱" },
  { code: "am", label: "Amharic", native: "አማርኛ", flag: "🇪🇹" },
  { code: "he", label: "Hebrew", native: "עברית", flag: "🇮🇱" },
];

const STORAGE_KEY = "metroshine_lang";
const ALL_CODES = LANGUAGES.map((l) => l.code).join(",");

declare global {
  interface Window {
    google: any;
    googleTranslateElementInit: () => void;
  }
}

const setGoogleCookie = (lang: string) => {
  const value = lang === "en" ? "/en/en" : `/en/${lang}`;
  const domains = [window.location.hostname, "." + window.location.hostname.replace(/^www\./, "")];
  domains.forEach((d) => {
    document.cookie = `googtrans=${value};path=/;domain=${d}`;
    document.cookie = `googtrans=${value};path=/`;
  });
};

const loadGoogleTranslate = () => {
  if (document.getElementById("google-translate-script")) return;
  window.googleTranslateElementInit = () => {
    new window.google.translate.TranslateElement(
      {
        pageLanguage: "en",
        includedLanguages: ALL_CODES,
        autoDisplay: false,
        layout: window.google.translate.TranslateElement.InlineLayout.SIMPLE,
      },
      "google_translate_element"
    );
  };
  const script = document.createElement("script");
  script.id = "google-translate-script";
  script.src = "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit";
  script.async = true;
  document.body.appendChild(script);
};

export const applyLanguage = (lang: string) => {
  localStorage.setItem(STORAGE_KEY, lang);
  setGoogleCookie(lang);
  loadGoogleTranslate();
  // Reload to apply translation cleanly
  if (typeof window !== "undefined") {
    setTimeout(() => window.location.reload(), 50);
  }
};

export const getCurrentLanguage = (): string => {
  return localStorage.getItem(STORAGE_KEY) || "en";
};

const LanguageSelector = () => {
  const [open, setOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    const chosen = localStorage.getItem(STORAGE_KEY);
    if (!chosen) {
      // Show on first visit, after brief delay so loading screen finishes
      const t = setTimeout(() => setOpen(true), 2400);
      return () => clearTimeout(t);
    } else {
      // Re-apply translation on subsequent visits
      if (chosen !== "en") {
        setGoogleCookie(chosen);
        loadGoogleTranslate();
      }
    }
  }, []);

  // Listen for external open events (from navbar button)
  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("open-language-selector", handler);
    return () => window.removeEventListener("open-language-selector", handler);
  }, []);

  const handleSelect = (code: string) => {
    setOpen(false);
    applyLanguage(code);
  };

  const popular = LANGUAGES.filter((l) => l.popular);
  const rest = LANGUAGES.filter((l) => !l.popular);
  const current = getCurrentLanguage();

  const renderCard = (lang: typeof LANGUAGES[number], i: number) => (
    <motion.button
      key={lang.code}
      onClick={() => handleSelect(lang.code)}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.05 + i * 0.025, duration: 0.4, ease: [0.25, 0.4, 0.25, 1] }}
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.97 }}
      className={`group relative flex items-center gap-3 p-3.5 rounded-2xl border text-left overflow-hidden transition-all duration-300 ${
        current === lang.code
          ? "border-primary/60 bg-primary/[0.07] shadow-lg shadow-primary/10"
          : "border-border/30 bg-card/20 hover:border-primary/40 hover:bg-card/50"
      }`}
    >
      {/* Hover shimmer */}
      <span className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-primary/10 to-transparent" />
      <span className="relative w-9 h-9 rounded-xl bg-background/60 border border-border/30 flex items-center justify-center text-lg shrink-0">
        {lang.flag}
      </span>
      <div className="relative flex-1 min-w-0">
        <div className="text-[13px] font-display font-semibold truncate tracking-tight">{lang.native}</div>
        <div className="text-[10px] text-muted-foreground/80 truncate tracking-wide">{lang.label}</div>
      </div>
      {current === lang.code && (
        <span className="relative w-5 h-5 rounded-full gold-gradient-bg flex items-center justify-center shrink-0">
          <Check size={11} className="text-primary-foreground" strokeWidth={3} />
        </span>
      )}
    </motion.button>
  );

  return (
    <>
      {/* Hidden Google Translate mount */}
      <div id="google_translate_element" style={{ display: "none" }} />

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Cinematic backdrop */}
            <motion.div
              className="absolute inset-0 bg-background/85 backdrop-blur-xl"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />
            {/* Ambient gold glows */}
            <motion.div
              className="absolute top-1/4 left-1/4 w-[500px] h-[500px] rounded-full pointer-events-none"
              style={{ background: "radial-gradient(circle, hsl(43 72% 52% / 0.18) 0%, transparent 60%)" }}
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 1.2, ease: "easeOut" }}
            />
            <motion.div
              className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full pointer-events-none"
              style={{ background: "radial-gradient(circle, hsl(43 72% 52% / 0.12) 0%, transparent 60%)" }}
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 1.4, delay: 0.1, ease: "easeOut" }}
            />

            <motion.div
              className="relative w-full max-w-3xl max-h-[92vh] notranslate"
              initial={{ opacity: 0, y: 40, scale: 0.94 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.94 }}
              transition={{ duration: 0.6, ease: [0.25, 0.4, 0.25, 1] }}
            >
              {/* Outer gold border glow */}
              <div className="absolute -inset-px rounded-[28px] bg-gradient-to-br from-primary/40 via-primary/10 to-primary/30 opacity-60 blur-[2px]" />

              <div className="relative rounded-[28px] overflow-hidden glass-strong border border-primary/20 shadow-[0_30px_80px_-20px_hsl(0_0%_0%/0.8)]">
                {/* Top accent gradient line */}
                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/80 to-transparent" />
                {/* Subtle texture */}
                <div className="absolute inset-0 opacity-[0.03] pointer-events-none mesh-gradient" />

                <div className="relative max-h-[92vh] overflow-y-auto p-7 md:p-10">
                  <button
                    onClick={() => {
                      setOpen(false);
                      if (!localStorage.getItem(STORAGE_KEY)) localStorage.setItem(STORAGE_KEY, "en");
                    }}
                    className="absolute top-5 right-5 w-9 h-9 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground bg-background/40 hover:bg-card/80 border border-border/30 hover:border-primary/40 transition-all backdrop-blur-sm z-10"
                    aria-label="Close"
                  >
                    <X size={16} />
                  </button>

                  {/* Header */}
                  <div className="flex flex-col items-center text-center mb-9">
                    <motion.div
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ duration: 0.7, delay: 0.1, type: "spring", stiffness: 180 }}
                      className="relative mb-5"
                    >
                      <div className="absolute inset-0 rounded-2xl gold-gradient-bg blur-xl opacity-50" />
                      <div className="relative w-16 h-16 rounded-2xl gold-gradient-bg flex items-center justify-center gold-glow-strong">
                        <Globe size={28} className="text-primary-foreground" strokeWidth={2} />
                      </div>
                    </motion.div>

                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.25, duration: 0.5 }}
                      className="text-[10px] tracking-[0.5em] uppercase text-primary/80 font-semibold mb-3"
                    >
                      — Welcome —
                    </motion.div>

                    <motion.h2
                      initial={{ opacity: 0, y: 12, filter: "blur(8px)" }}
                      animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                      transition={{ delay: 0.3, duration: 0.7 }}
                      className="font-display text-3xl md:text-4xl font-bold tracking-tight mb-3"
                    >
                      Choose your <span className="gold-gradient-text-animated">language</span>
                    </motion.h2>

                    <motion.p
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.45, duration: 0.5 }}
                      className="text-muted-foreground text-sm md:text-[15px] max-w-md leading-relaxed"
                    >
                      Experience Metroshine in your preferred language. You can change this anytime.
                    </motion.p>
                  </div>

                  {/* Section heading */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="h-px flex-1 bg-gradient-to-r from-transparent to-border/50" />
                    <span className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground/70 font-semibold">
                      Most popular
                    </span>
                    <div className="h-px flex-1 bg-gradient-to-l from-transparent to-border/50" />
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5 mb-8">
                    {popular.map((lang, i) => renderCard(lang, i))}
                  </div>

                  <AnimatePresence initial={false}>
                    {!showAll ? (
                      <motion.button
                        key="show-more"
                        onClick={() => setShowAll(true)}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="w-full py-3.5 text-[11px] font-semibold tracking-[0.3em] uppercase text-muted-foreground hover:text-primary border border-border/30 hover:border-primary/40 rounded-2xl transition-all duration-300 hover:bg-card/30"
                      >
                        + Show more languages
                      </motion.button>
                    ) : (
                      <motion.div
                        key="more-grid"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.5, ease: [0.25, 0.4, 0.25, 1] }}
                      >
                        <div className="flex items-center gap-3 mb-4">
                          <div className="h-px flex-1 bg-gradient-to-r from-transparent to-border/50" />
                          <span className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground/70 font-semibold">
                            More languages
                          </span>
                          <div className="h-px flex-1 bg-gradient-to-l from-transparent to-border/50" />
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5">
                          {rest.map((lang, i) => renderCard(lang, i))}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Footer attribution */}
                  <div className="mt-8 pt-5 border-t border-border/20 flex items-center justify-center gap-2 text-[10px] text-muted-foreground/50 tracking-wide">
                    <Globe size={10} />
                    <span>Powered translations • Metroshine luxury detailing</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default LanguageSelector;
